import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, MapPin, CalendarClock, History, Navigation, Route, Calendar, AlertCircle, UserPlus, TrendingUp } from 'lucide-react';
import { AgentLayout } from '../../components/AgentLayout';
import { AddMerchantSheet } from '../../components/AddMerchantSheet';
import api from '../../api';
import { getGPS } from '../../gps';
import { haversineKm, fmtDistance, overdueTier, overdueTierStyle, relativePast, latestVisit, truncate } from '../../lib/agent-helpers';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';

const STATUS_OPTIONS = { New: ['Contacted', 'Onboarded'], Contacted: ['Onboarded'], Onboarded: [] };

function formatDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return d; }
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date(new Date().toDateString());
}

function daysOverdue(dateStr) {
  if (!dateStr) return 0;
  const diff = new Date(new Date().toDateString()) - new Date(dateStr);
  return Math.floor(diff / 86400000);
}

function openMaps(lat, lng) {
  if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
}

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`}>{msg}</div>;
}

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem('au_user') || '{}');
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [showSheet, setShowSheet] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [historyCard, setHistoryCard] = useState(null);
  const [rescheduleCard, setRescheduleCard] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [statusChanging, setStatusChanging] = useState(null);
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [loading, setLoading] = useState(true);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadData = useCallback(async () => {
    try {
      const [statsRes, merchantsRes] = await Promise.all([
        api.get('/dashboard/agent'),
        api.get('/merchants?limit=10')
      ]);
      setStats(statsRes.data);
      setMerchants(merchantsRes.data.merchants || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const { indicator: pullIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    if (searchParams.get('addMerchant') === 'true') setShowSheet(true);
  }, [searchParams]);

  const getDayGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getDateStr = () => new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const handleStatusChange = async (merchantId, newStatus) => {
    setStatusChanging(merchantId);
    try {
      await api.patch(`/merchants/${merchantId}/status`, { status: newStatus });
      setMerchants(prev => prev.map(m => m.id === merchantId ? { ...m, status: newStatus } : m));
      showToast(`Status updated to ${newStatus}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusChanging(null);
    }
  };

  const handleMerchantAdded = async () => {
    showToast('Merchant registered successfully!');
    await loadData();
  };

  const handleEditSuccess = async () => {
    showToast('Merchant updated successfully!');
    setEditingMerchant(null);
    setShowSheet(false);
    await loadData();
  };

  const handleReschedule = async (merchantId) => {
    if (!rescheduleDate) return;
    try {
      await api.post(`/merchants/${merchantId}/visit-log`, {
        note: rescheduleNote || `Follow-up rescheduled to ${rescheduleDate}`,
        rescheduleDate
      });
      showToast('Follow-up rescheduled');
      setRescheduleCard(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await loadData();
    } catch {
      showToast('Failed to reschedule');
    }
  };

  const handleLogOnly = async (merchantId) => {
    if (!rescheduleNote.trim()) { showToast('Add a note to log this visit'); return; }
    try {
      await api.post(`/merchants/${merchantId}/visit-log`, { note: rescheduleNote });
      showToast('Visit logged');
      setRescheduleCard(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await loadData();
    } catch {
      showToast('Failed to log visit');
    }
  };

  const openAddSheet = () => {
    setEditingMerchant(null);
    setShowSheet(true);
  };

  const openEditSheet = (merchant) => {
    setEditingMerchant(merchant);
    setShowSheet(true);
  };

  if (loading) return (
    <div className="au-agent-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div style={{ color: 'var(--navy)', fontFamily: 'var(--fh)', fontSize: '16px' }}>Loading…</div>
    </div>
  );

  const overdueItems = stats?.overdueFollowups || [];
  const todayItems = stats?.todayFollowups || [];
  const totalFollowups = overdueItems.length + todayItems.length;
  const agentGps = getGPS();

  // Today's Plan: build ordered list of stops (today first, then overdue) with GPS,
  // nearest-first when agent GPS is available. Capped at 10 for Google Maps free tier.
  const stopsForRoute = (() => {
    const all = [...todayItems, ...overdueItems]
      .filter(m => m.gps_lat && m.gps_lng)
      .map(m => ({
        id: m.id,
        name: m.businessName,
        lat: m.gps_lat,
        lng: m.gps_lng,
        dist: agentGps ? haversineKm(agentGps, { lat: m.gps_lat, lng: m.gps_lng }) : null,
      }));
    // De-dup by id (a merchant could appear in both today + overdue if data is odd)
    const seen = new Set();
    const unique = all.filter(s => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
    if (agentGps) {
      unique.sort((a, b) => (a.dist ?? 9999) - (b.dist ?? 9999));
    }
    return unique.slice(0, 10);
  })();

  const handlePlanRoute = () => {
    if (stopsForRoute.length === 0) {
      showToast('No merchants with GPS for today');
      return;
    }
    const origin = agentGps ? `${agentGps.lat},${agentGps.lng}` : 'Current+Location';
    // Last stop is destination; everything before is waypoints
    const last = stopsForRoute[stopsForRoute.length - 1];
    const destination = `${last.lat},${last.lng}`;
    const waypoints = stopsForRoute.slice(0, -1).map(s => `${s.lat},${s.lng}`).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${destination}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
    window.open(url, '_blank');
  };

  const totalPlanned = todayItems.length + overdueItems.length;
  const newLeads = stats?.todayVisits ?? 0;

  return (
    <AgentLayout onAddClick={openAddSheet} overdueCount={overdueItems.length}>
      {pullIndicator}
      <div className="au-page">
        {/* Greeting */}
        <div className="au-greeting fu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="greet-hi">{getDayGreeting()}, {user.name?.split(' ')[0]}</div>
            <div className="greet-dt">{getDateStr()}</div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/my-performance')}
            data-testid="my-performance-btn"
            title="My activity & collection stats"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--navy)', color: '#fff', border: 'none',
              borderRadius: 999, padding: '8px 12px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 4px 10px rgba(11,37,69,0.18)',
            }}
          >
            <TrendingUp size={14} /> My Stats
          </button>
        </div>

        {/* Today's Plan */}
        <div className="tp-card" data-testid="todays-plan-card">
          <div className="tp-hd">
            <div className="tp-hd-l">
              <div className="tp-title">Today's Plan</div>
              <div className="tp-sub">
                {totalPlanned === 0 && newLeads === 0
                  ? 'No visits scheduled — a great day to find new leads!'
                  : totalPlanned === 0
                    ? `${newLeads} new lead${newLeads > 1 ? 's' : ''} added today`
                    : `${totalPlanned} visit${totalPlanned > 1 ? 's' : ''} on your list`}
              </div>
            </div>
          </div>
          <div className="tp-pills">
            <div className="tp-pill" onClick={() => navigate('/followups')}>
              <Calendar size={14} />
              <span className="tp-pn">{todayItems.length}</span>
              <span className="tp-pl">Today</span>
            </div>
            <div
              className={`tp-pill ${overdueItems.length > 0 ? 'urgent' : ''}`}
              onClick={() => navigate('/followups')}
              data-testid="tp-pill-overdue"
            >
              <AlertCircle size={14} />
              <span className="tp-pn">{overdueItems.length}</span>
              <span className="tp-pl">Overdue</span>
            </div>
            <div className="tp-pill" onClick={() => navigate('/merchants?today=true')}>
              <UserPlus size={14} />
              <span className="tp-pn">{newLeads}</span>
              <span className="tp-pl">Added</span>
            </div>
          </div>
          <button
            type="button"
            className="tp-route-btn"
            onClick={handlePlanRoute}
            disabled={stopsForRoute.length === 0}
            data-testid="plan-route-btn"
          >
            <Route size={16} />
            {stopsForRoute.length === 0
              ? 'No GPS-tagged stops yet'
              : `Plan route to ${stopsForRoute.length} stop${stopsForRoute.length > 1 ? 's' : ''} in Google Maps`}
          </button>
          {stopsForRoute.length > 0 && (
            <div className="tp-route-hint">
              {agentGps ? 'Sorted nearest-first from your location' : 'Allow location to optimize order'}
              {totalPlanned > 10 && ' · showing closest 10'}
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="au-stats" data-testid="agent-stats">
          <div 
            className="au-stile acc fu-1" 
            onClick={() => navigate('/merchants?service=upi_qr')}
            data-testid="stat-upi"
          >
            <div className="slbl">UPI/QR Leads</div>
            <div className="sval red">{stats?.upiQr ?? 0}</div>
            <div className="ssub">My total</div>
          </div>
          <div 
            className="au-stile fu-2" 
            onClick={() => navigate('/merchants')}
            data-testid="stat-merchants"
          >
            <div className="slbl">My Merchants</div>
            <div className="sval">{stats?.total ?? 0}</div>
            <div className="ssub">All registered</div>
          </div>
          <div 
            className="au-stile fu-3" 
            onClick={() => navigate('/merchants?today=true')}
            data-testid="stat-today"
          >
            <div className="slbl">Added Today</div>
            <div className="sval">{stats?.todayVisits ?? 0}</div>
            <div className="ssub">Keep going!</div>
          </div>
          <div 
            className="au-stile fu-4" 
            onClick={() => navigate('/followups')}
            data-testid="stat-followups"
          >
            <div className="slbl">Follow-ups</div>
            <div className="sval">{totalFollowups}</div>
            <div className="ssub" style={{ color: overdueItems.length > 0 ? 'var(--red)' : undefined }}>
              {overdueItems.length > 0 ? `${overdueItems.length} overdue` : 'All on track'}
            </div>
          </div>
        </div>

        {/* Overdue Banner */}
        {overdueItems.length > 0 && (
          <div className="ov-ban fu" data-testid="overdue-banner">
            <div className="ov-ico">📅</div>
            <div className="ov-body">
              <div className="ov-title">
                {overdueItems.length} follow-up{overdueItems.length > 1 ? 's' : ''} overdue
              </div>
              {overdueItems.slice(0, 3).map(m => {
                const dOver = daysOverdue(m.nextVisitDate);
                const tier = overdueTier(dOver);
                return (
                  <div key={m.id} className="ov-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ov-nm">{m.businessName}</div>
                      <a href={`tel:${m.phone}`} className="ov-phone" onClick={e => e.stopPropagation()}>
                        <Phone size={10} /> {m.phone}
                      </a>
                    </div>
                    <div className="ov-right">
                      <span className="ov-tag" style={{ ...overdueTierStyle(tier) }}>{dOver}d late</span>
                      {m.gps_lat && (
                        <button 
                          className="ov-gps-btn" 
                          onClick={(e) => { e.stopPropagation(); openMaps(m.gps_lat, m.gps_lng); }}
                        >
                          <Navigation size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* My Recent Merchants */}
        <div className="sec-hd fu">
          <span className="sec-t">My Recent Merchants</span>
          <span className="sec-l" onClick={() => navigate('/merchants')} data-testid="see-all-merchants">See all →</span>
        </div>

        {merchants.length === 0 ? (
          <div className="empty-state fu">
            <div className="empty-icon">🏪</div>
            <div className="empty-title">No merchants yet</div>
            <div className="empty-text">Tap + to register your first merchant</div>
          </div>
        ) : (
          merchants.slice(0, 5).map((m, idx) => {
            const showHistory = historyCard === m.id;
            const showReschedule = rescheduleCard === m.id;
            const nextOptions = STATUS_OPTIONS[m.status] || [];
            const hasGPS = m.gps_lat && m.gps_lng;
            const dOver = isOverdue(m.nextVisitDate) ? daysOverdue(m.nextVisitDate) : 0;
            const tier = overdueTier(dOver);
            const distKm = (agentGps && hasGPS) ? haversineKm(agentGps, { lat: m.gps_lat, lng: m.gps_lng }) : null;
            const lv = latestVisit(m.visitLog);

            return (
              <div key={m.id} className={`mcard fu${idx % 5}`} data-testid={`merchant-card-${m.id}`}>
                <div className="mc-body">
                  <div className="mc-top">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="mc-nm">{m.businessName}</div>
                      <div className="mc-ow">{m.ownerName}</div>
                    </div>
                    <span className={`badge-${(m.status || 'new').toLowerCase()}`}>{m.status || 'New'}</span>
                  </div>
                  {lv && (
                    <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '4px', display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                      <History size={11} style={{ marginTop: '1px', flexShrink: 0 }} />
                      <span><b style={{ color: 'var(--g2)' }}>Last visit {relativePast(lv.visitedAt || lv.addedAt)}:</b> {truncate(lv.note, 70) || '(no note)'}</span>
                    </div>
                  )}
                  <div className="mc-meta">
                    <a href={`tel:${m.phone}`} className="mc-mi clickable" onClick={e => e.stopPropagation()}>
                      <Phone size={12} /> {m.phone}
                    </a>
                    {hasGPS && (
                      <span 
                        className="gps-ok clickable" 
                        onClick={(e) => { e.stopPropagation(); openMaps(m.gps_lat, m.gps_lng); }}
                      >
                        <MapPin size={12} /> GPS
                      </span>
                    )}
                    {distKm != null && (
                      <span className="mc-mi" style={{ color: 'var(--navy)', fontWeight: 600 }}>
                        📍 {fmtDistance(distKm)} away
                      </span>
                    )}
                  </div>
                  {(m.services || []).length > 0 && (
                    <div className="mc-tags">
                      {m.services.slice(0, 3).map(s => (
                        <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>
                      ))}
                      {m.services.length > 3 && <span className="stag">+{m.services.length - 3}</span>}
                    </div>
                  )}
                  {m.status === 'Onboarded' && m.onboardedAt && (
                    <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '5px', fontWeight: 600 }}>
                      Onboarded: {formatDate(m.onboardedAt)}
                    </div>
                  )}
                </div>
                {m.nextVisitDate && (
                  <div className="mc-foot">
                    <span className="mc-vl">Next Visit</span>
                    {tier ? (
                      <span style={{ ...overdueTierStyle(tier), padding: '3px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700 }}>
                        {formatDate(m.nextVisitDate)} · {dOver}d overdue
                      </span>
                    ) : (
                      <span className="mc-vd">{formatDate(m.nextVisitDate)}</span>
                    )}
                  </div>
                )}
                {/* Actions Row */}
                <div className="mc-actions">
                  {nextOptions.length > 0 && (
                    <select
                      className="mc-status-sel"
                      value=""
                      onChange={e => e.target.value && handleStatusChange(m.id, e.target.value)}
                      disabled={statusChanging === m.id}
                      data-testid={`status-select-${m.id}`}
                    >
                      <option value="">Status: {m.status}</option>
                      {nextOptions.map(s => <option key={s} value={s}>→ {s}</option>)}
                    </select>
                  )}
                  <button
                    className="mc-act-btn"
                    onClick={() => openEditSheet(m)}
                    data-testid={`edit-btn-${m.id}`}
                  >
                    Edit
                  </button>
                  <button
                    className="mc-act-btn"
                    onClick={() => { setRescheduleCard(showReschedule ? null : m.id); setRescheduleDate(''); setRescheduleNote(''); }}
                    data-testid={`reschedule-btn-${m.id}`}
                  >
                    <CalendarClock size={12} /> Reschedule
                  </button>
                  <button
                    className="mc-act-btn"
                    onClick={() => setHistoryCard(showHistory ? null : m.id)}
                    data-testid={`history-btn-${m.id}`}
                  >
                    <History size={12} /> History
                  </button>
                </div>
                {/* Reschedule Inline */}
                {showReschedule && (
                  <div className="mc-reschedule" data-testid={`reschedule-panel-${m.id}`}>
                    <div className="mc-reschedule-title">Reschedule Follow-up</div>
                    <textarea className="fi" rows={2} placeholder="Add notes about this visit..."
                      value={rescheduleNote} onChange={e => setRescheduleNote(e.target.value)}
                      style={{ marginBottom: '8px', fontSize: '12px' }} />
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--g3)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarClock size={11} /> Next visit date
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      {[
                        { label: 'Tomorrow', days: 1 },
                        { label: 'In 3 days', days: 3 },
                        { label: 'Next week', days: 7 },
                      ].map(p => {
                        const d = new Date(); d.setDate(d.getDate() + p.days);
                        const iso = d.toISOString().slice(0, 10);
                        const active = rescheduleDate === iso;
                        return (
                          <button key={p.label} type="button" onClick={() => setRescheduleDate(iso)}
                            style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '999px', cursor: 'pointer',
                              border: active ? '1px solid var(--navy)' : '1px solid var(--g2)',
                              background: active ? 'var(--navy)' : '#fff',
                              color: active ? '#fff' : 'var(--navy)', fontWeight: 600 }}>{p.label}</button>
                        );
                      })}
                    </div>
                    <div className="mc-reschedule-row">
                      <input type="date" className="fi" value={rescheduleDate}
                        min={new Date().toISOString().slice(0,10)}
                        onChange={e => setRescheduleDate(e.target.value)}
                        data-testid={`reschedule-date-${m.id}`} />
                      <button className="btn-navy" onClick={() => handleReschedule(m.id)}
                        disabled={!rescheduleDate}
                        data-testid={`reschedule-save-${m.id}`}>Save</button>
                    </div>
                    <div style={{ marginTop: '6px', textAlign: 'right' }}>
                      <button type="button" onClick={() => handleLogOnly(m.id)}
                        disabled={!rescheduleNote.trim()}
                        data-testid={`log-only-${m.id}`}
                        style={{ background: 'none', border: 'none', color: 'var(--navy)', fontSize: '11px', fontWeight: 600, cursor: rescheduleNote.trim() ? 'pointer' : 'not-allowed', opacity: rescheduleNote.trim() ? 1 : 0.45, textDecoration: 'underline' }}>
                        + Log note only (don't reschedule)
                      </button>
                    </div>
                  </div>
                )}
                {/* Visit History Panel */}
                {showHistory && (
                  <div className="mc-history">
                    <div className="mc-hist-title">Visit History ({m.visitLog?.length || 0})</div>
                    {(!m.visitLog || m.visitLog.length === 0) ? (
                      <div className="mc-hist-empty">No visits recorded yet</div>
                    ) : (
                      m.visitLog.slice(-3).reverse().map((v, i) => (
                        <div key={i} className="mc-hist-item">
                          <div className="mc-hist-date">{new Date(v.visitedAt || v.addedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                          <div className="mc-hist-note">{v.note}</div>
                        </div>
                      ))
                    )}
                    {m.visitLog?.length > 3 && (
                      <div className="mc-hist-more" onClick={() => openEditSheet(m)}>
                        View all {m.visitLog.length} notes →
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <AddMerchantSheet
        isOpen={showSheet}
        onClose={() => { setShowSheet(false); setEditingMerchant(null); }}
        onSuccess={editingMerchant ? handleEditSuccess : handleMerchantAdded}
        editMerchant={editingMerchant}
      />

      <Toast msg={toast.msg} show={toast.show} />
    </AgentLayout>
  );
}
